import React, { useState, useEffect } from 'react';
import { Save, Upload, Building2, Globe, DollarSign, FileText, Palette, Clock } from 'lucide-react';
import { API_URL, getServerUrl } from '../../services/api';

function ParametresBijouterie() {
  const [parametres, setParametres] = useState({
    nom_entreprise: '',
    slogan: '',
    logo_url: '',
    image_banniere_url: '',
    adresse: '',
    ville: '',
    code_postal: '',
    pays: 'Maroc',
    telephone: '',
    email: '',
    site_web: '',
    devise: 'DH',
    symbole_devise: 'DH',
    tva_defaut: 20.00,
    numero_registre_commerce: '',
    numero_ice: '',
    numero_if: '',
    numero_patente: '',
    iban: '',
    swift_bic: '',
    banque_nom: '',
    conditions_generales: '',
    mentions_legales: '',
    couleur_primaire: '#4F46E5',
    couleur_secondaire: '#10B981',
    fuseau_horaire: 'Africa/Casablanca',
    langue_defaut: 'fr',
    format_date: 'DD/MM/YYYY',
    format_heure: 'HH:mm'
  });

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    loadParametres();
  }, []);

  const loadParametres = async () => {
    try {
      const response = await fetch(`${API_URL}/parametres/bijouterie`);
      if (response.ok) {
        const data = await response.json();
        setParametres(data);
      }
    } catch (error) {
      console.error('Erreur chargement paramètres:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/parametres/bijouterie`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(parametres)
      });

      if (response.ok) {
        alert('Paramètres enregistrés avec succès !');
      } else {
        alert('Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const endpoint = type === 'logo' ? 'upload-logo' : 'upload-banniere';
      const response = await fetch(`${API_URL}/parametres/bijouterie/${endpoint}`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setParametres(prev => ({
          ...prev,
          [type === 'logo' ? 'logo_url' : 'image_banniere_url']: data[type === 'logo' ? 'logo_url' : 'image_banniere_url']
        }));
        alert('Image uploadée avec succès !');
      }
    } catch (error) {
      console.error('Erreur upload:', error);
      alert('Erreur lors de l\'upload');
    }
  };

  const tabs = [
    { id: 'general', label: 'Informations Générales', icon: Building2 },
    { id: 'contact', label: 'Contact & Localisation', icon: Globe },
    { id: 'finance', label: 'Finances & Comptabilité', icon: DollarSign },
    { id: 'legal', label: 'Mentions Légales', icon: FileText },
    { id: 'apparence', label: 'Apparence', icon: Palette },
    { id: 'regional', label: 'Paramètres Régionaux', icon: Clock }
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Paramètres de la Bijouterie</h1>
          <p className="text-gray-600">Configurez les informations de votre entreprise</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-4 px-6" aria-label="Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 py-4 px-3 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            {/* Onglet Général */}
            {activeTab === 'general' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Informations Générales</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom de l'entreprise *
                    </label>
                    <input
                      type="text"
                      value={parametres.nom_entreprise}
                      onChange={(e) => setParametres({...parametres, nom_entreprise: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Slogan
                    </label>
                    <input
                      type="text"
                      value={parametres.slogan || ''}
                      onChange={(e) => setParametres({...parametres, slogan: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Logo de l'entreprise
                    </label>
                    {parametres.logo_url && (
                      <img src={getServerUrl(parametres.logo_url)} alt="Logo" className="h-20 mb-2" />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload(e, 'logo')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Image bannière
                    </label>
                    {parametres.image_banniere_url && (
                      <img src={getServerUrl(parametres.image_banniere_url)} alt="Bannière" className="h-20 mb-2" />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload(e, 'banniere')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Onglet Contact */}
            {activeTab === 'contact' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Contact & Localisation</h2>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Adresse</label>
                  <textarea
                    value={parametres.adresse || ''}
                    onChange={(e) => setParametres({...parametres, adresse: e.target.value})}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ville</label>
                    <input
                      type="text"
                      value={parametres.ville || ''}
                      onChange={(e) => setParametres({...parametres, ville: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Code Postal</label>
                    <input
                      type="text"
                      value={parametres.code_postal || ''}
                      onChange={(e) => setParametres({...parametres, code_postal: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pays</label>
                    <input
                      type="text"
                      value={parametres.pays || ''}
                      onChange={(e) => setParametres({...parametres, pays: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
                    <input
                      type="tel"
                      value={parametres.telephone || ''}
                      onChange={(e) => setParametres({...parametres, telephone: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={parametres.email || ''}
                      onChange={(e) => setParametres({...parametres, email: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Site Web</label>
                    <input
                      type="url"
                      value={parametres.site_web || ''}
                      onChange={(e) => setParametres({...parametres, site_web: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Onglet Finance */}
            {activeTab === 'finance' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Finances & Comptabilité</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Devise</label>
                    <input
                      type="text"
                      value={parametres.devise || ''}
                      onChange={(e) => setParametres({...parametres, devise: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Symbole Devise</label>
                    <input
                      type="text"
                      value={parametres.symbole_devise || ''}
                      onChange={(e) => setParametres({...parametres, symbole_devise: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">TVA par défaut (%)</label>
                    <input
                      type="text" inputMode="decimal" onFocus={e => e.target.select()}
                      step="0.01"
                      value={parametres.tva_defaut || ''}
                      onChange={(e) => setParametres({...parametres, tva_defaut: parseFloat(e.target.value)})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">N° Registre Commerce</label>
                    <input
                      type="text"
                      value={parametres.numero_registre_commerce || ''}
                      onChange={(e) => setParametres({...parametres, numero_registre_commerce: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">N° ICE</label>
                    <input
                      type="text"
                      value={parametres.numero_ice || ''}
                      onChange={(e) => setParametres({...parametres, numero_ice: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Identifiant Fiscal (IF)</label>
                    <input
                      type="text"
                      value={parametres.numero_if || ''}
                      onChange={(e) => setParametres({...parametres, numero_if: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">N° Patente</label>
                    <input
                      type="text"
                      value={parametres.numero_patente || ''}
                      onChange={(e) => setParametres({...parametres, numero_patente: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">IBAN</label>
                    <input
                      type="text"
                      value={parametres.iban || ''}
                      onChange={(e) => setParametres({...parametres, iban: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SWIFT/BIC</label>
                    <input
                      type="text"
                      value={parametres.swift_bic || ''}
                      onChange={(e) => setParametres({...parametres, swift_bic: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nom de la Banque</label>
                    <input
                      type="text"
                      value={parametres.banque_nom || ''}
                      onChange={(e) => setParametres({...parametres, banque_nom: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Onglet Légal */}
            {activeTab === 'legal' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Mentions Légales</h2>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Conditions Générales</label>
                  <textarea
                    value={parametres.conditions_generales || ''}
                    onChange={(e) => setParametres({...parametres, conditions_generales: e.target.value})}
                    rows="6"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mentions Légales</label>
                  <textarea
                    value={parametres.mentions_legales || ''}
                    onChange={(e) => setParametres({...parametres, mentions_legales: e.target.value})}
                    rows="6"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            )}

            {/* Onglet Apparence */}
            {activeTab === 'apparence' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Apparence</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Couleur Primaire</label>
                    <div className="flex items-center space-x-4">
                      <input
                        type="color"
                        value={parametres.couleur_primaire || '#4F46E5'}
                        onChange={(e) => setParametres({...parametres, couleur_primaire: e.target.value})}
                        className="h-10 w-20 border border-gray-300 rounded"
                      />
                      <input
                        type="text"
                        value={parametres.couleur_primaire || ''}
                        onChange={(e) => setParametres({...parametres, couleur_primaire: e.target.value})}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Couleur Secondaire</label>
                    <div className="flex items-center space-x-4">
                      <input
                        type="color"
                        value={parametres.couleur_secondaire || '#10B981'}
                        onChange={(e) => setParametres({...parametres, couleur_secondaire: e.target.value})}
                        className="h-10 w-20 border border-gray-300 rounded"
                      />
                      <input
                        type="text"
                        value={parametres.couleur_secondaire || ''}
                        onChange={(e) => setParametres({...parametres, couleur_secondaire: e.target.value})}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Onglet Régional */}
            {activeTab === 'regional' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Paramètres Régionaux</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fuseau Horaire</label>
                    <select
                      value={parametres.fuseau_horaire || ''}
                      onChange={(e) => setParametres({...parametres, fuseau_horaire: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="Africa/Casablanca">Africa/Casablanca</option>
                      <option value="Europe/Paris">Europe/Paris</option>
                      <option value="UTC">UTC</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Langue par défaut</label>
                    <select
                      value={parametres.langue_defaut || ''}
                      onChange={(e) => setParametres({...parametres, langue_defaut: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="fr">Français</option>
                      <option value="ar">Arabe</option>
                      <option value="en">Anglais</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Format de Date</label>
                    <select
                      value={parametres.format_date || ''}
                      onChange={(e) => setParametres({...parametres, format_date: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                      <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Format d'Heure</label>
                    <select
                      value={parametres.format_heure || ''}
                      onChange={(e) => setParametres({...parametres, format_heure: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="HH:mm">24 heures (HH:mm)</option>
                      <option value="hh:mm A">12 heures (hh:mm AM/PM)</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Bouton Enregistrer */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="flex items-center space-x-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              <Save className="h-5 w-5" />
              <span>{loading ? 'Enregistrement...' : 'Enregistrer les paramètres'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ParametresBijouterie;
